/**
 * Copyright (c) 2010-2024 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AsyncPipe, DatePipe, DecimalPipe } from '@angular/common';
import { Component, computed, effect, inject, input, OnDestroy, OnInit, Signal, untracked, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatSort, MatSortHeader, Sort } from '@angular/material/sort';
import {
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatFooterCell,
    MatFooterCellDef,
    MatFooterRow,
    MatFooterRowDef,
    MatHeaderCell,
    MatHeaderCellDef,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatTable
} from '@angular/material/table';
import { MatTooltip } from '@angular/material/tooltip';
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { Store } from '@ngxs/store';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, filter, Observable, Subject } from 'rxjs';
import { concatMap, distinctUntilChanged, first, map, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { TableLimits } from '../../../core/state/appConfig/app-config.model';
import { AppConfigState } from '../../../core/state/appConfig/app-config.state';
import { PageState } from '../../../core/state/page/page.state';
import { ZoomButtonComponent } from '../../../core/ui/zoomButton/zoom-button.component';
import { PagingOptions } from '../../../types/PagingOptions';
import { SortOptions } from '../../../types/SortOptions';
import { QueryService } from '../../map/services/query.service';
import { LoggerService } from '../../shared/services/logger.service';
import { SearchService } from '../services/search.service';
import { SetSearchPagingOptions, SetSearchSortOptions } from '../state/search.actions';
import { SearchDefinition, SearchFormModel } from '../state/search.model';
import { SearchState } from '../state/search.state';
import { PrintSearchComponent } from './print/print-search.component';
import { SearchResultsContentDataSource } from './search-results-content-data.source';

@Component({
    selector: 'dash-gis-search-results-content',
    templateUrl: './search-results-content.component.html',
    styleUrls: [ './search-results-content.component.scss' ],
    imports: [
        AsyncPipe,
        DatePipe,
        DecimalPipe,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        MatCell,
        MatCellDef,
        MatColumnDef,
        MatFooterCell,
        MatFooterCellDef,
        MatFooterRow,
        MatFooterRowDef,
        MatHeaderCell,
        MatHeaderCellDef,
        MatHeaderRow,
        MatHeaderRowDef,
        MatIcon,
        MatIconButton,
        MatPaginator,
        MatProgressBar,
        MatRow,
        MatRowDef,
        MatSort,
        MatSortHeader,
        MatTable,
        MatTooltip,
        ZoomButtonComponent
    ]
})
export class SearchResultsContentComponent {
    searchName: Signal<string>;
    searchParams: Signal<SearchFormModel>;
    paging: Signal<PagingOptions>;
    sorting: Signal<SortOptions>;
    displayedColumns: Signal<string[]>;

    dataSource: SearchResultsContentDataSource;
    pageOptions: TableLimits;

    instanceId = input.required<string>();
    forPrint = input<boolean>(false);

    definition: Signal<SearchDefinition>;
    initialized: Signal<boolean>;
    private _criteria$: Observable<(arg0: string) => SearchFormModel> = inject(Store).select(SearchState.currentCriteria);
    private _paging$: Observable<(arg0: string) => PagingOptions> = inject(Store).select(SearchState.paging);
    private _sorting$: Observable<(arg0: string) => SortOptions> = inject(Store).select(SearchState.sorting);

    @ViewChild(MatPaginator) private paginator: MatPaginator;
    @ViewChild('table') private table: MatTable<any>;

    constructor(
        private searchService: SearchService,
        private queryService: QueryService,
        private logger: LoggerService,
        private store: Store
    ) {
        this.pageOptions = this.store.selectSnapshot(AppConfigState.tableLimits);
        const definitionFn = toSignal(inject(Store).select(SearchState.currentDefinition));
        this.definition = computed<SearchDefinition>(() => definitionFn()(this.instanceId()));
        const criteriaFn = toSignal(this._criteria$);
        this.searchParams = computed(() => criteriaFn()(this.instanceId()));
        const pagingFn = toSignal(this._paging$);
        this.paging = computed(() => pagingFn()(this.instanceId()));
        const sortingFn = toSignal(this._sorting$);
        this.sorting = computed(() => sortingFn()(this.instanceId()));
        this.searchName = computed(() => this.definition()?.name);
        this.dataSource = new SearchResultsContentDataSource(this.logger, this.searchService, this.forPrint);
        this.displayedColumns = computed(() => {
            const cols = this.mapEnabled && !this.forPrint() ? ['zoom'] : [];
            this.definition()?.resultFields.forEach((col) => {
                if (col.hasOwnProperty('displayName') && col.displayName) {
                    cols.push(col.name);
                }
            });
            return cols;
        });
        this.initialized = computed(() => !!this.definition() && !!this.searchParams())
        effect(() => {
            const initialized = this.initialized();
            if (initialized) {
                if (this.forPrint() && this.initialized) {
                    this.dataSource.loadAllSearchResults(
                        this.searchName(),
                        this.definition(),
                        this.searchParams(),
                        this.sorting()?.column,
                        this.sorting()?.direction
                    );
                } else {
                    this.dataSource.loadSearchResults(
                        this.searchName(),
                        this.definition(),
                        this.searchParams(),
                        this.paging()?.page,
                        this.paging()?.limit,
                        this.sorting()?.column,
                        this.sorting()?.direction
                    );
                }
            }
        });
    }

    onPage(evt: PageEvent) {
        const options: PagingOptions = {
            page: evt.pageIndex,
            limit: evt.pageSize
        };
        this.store.dispatch(
            new SetSearchPagingOptions({
                id: this.instanceId(),
                reportName: this.definition.name,
                pagingOptions: options
            })
        );
    }

    onSort(sortEvent: Sort) {
        const options: SortOptions = {
            column: sortEvent.active,
            direction: sortEvent.direction
        };
        this.store.dispatch(
            new SetSearchSortOptions({
                id: this.instanceId(),
                reportName: this.definition.name,
                sortOptions: options
            })
        );
    }

    async downloadExcel() {
        this.searchService.runFullSearch(this.searchName(), this.searchParams(), this.sorting().column)
            .pipe(take(1))
            .subscribe((results) => {
                const excelData = this.searchService.prepareExcelData(results);
                this.searchService.downloadExcel(excelData);
            });
    }

    zoomTo(rowData) {
        this.logger.error('zoomTo');
        const searchDef = this.definition();
        const layerName = rowData.DB_LAYER_ID || searchDef.layer;
        const featureId = rowData.DB_FEATURE_ID;
        this.queryService.zoomTo(layerName, searchDef.objectkey, featureId);
    }

    totalsValue(totals, fieldName) {
        const total = totals.find((t) => t.name === fieldName);
        return total ? total.value : '';
    }

    averagesValue(averages, fieldName) {
        const average = averages.find((a) => a.name === fieldName);
        return average ? average.value : '';
    }

    getTotalsCellDisplay(fieldName: string, index: number): string {
        if (this.definition().totalFields) {
            if (index === 0) {
                return 'label';
            }

            if (-1 !== this.definition().totalFields.findIndex((f) => f === fieldName)) {
                return 'value';
            }
        }
    }

    getAveragesCellDisplay(fieldName: string, index: number): string {
        if (this.definition().averageFields) {
            if (index === 0) {
                return 'label';
            }

            if (-1 !== this.definition().averageFields.findIndex((f) => f === fieldName)) {
                return 'value';
            }
        }
    }

    get printComponentInputs() {
        return ({
            forPrint: true,
            instanceId: this.instanceId
        });
    }

    get totalsColumnsToDisplay(): string[] {
        return this.displayedColumns().map((name) => `total_${ name }`);
    }

    get averagesColumnsToDisplay(): string[] {
        return this.displayedColumns().map((name) => `average_${ name }`);
    }

    get mapEnabled() {
        return this.store.selectSnapshot(PageState.mapEnabled);
    }
}
